import dayjs from 'dayjs';
import { JobOpening as JobOpeningApi } from '../../dvinci-api/types/job-opening';

export default interface JobOpening {
  id: string;
  name: string;
  type: JobOpeningType;
  categories: Category[];
  reference?: string;
  location?: string /* deprecated */;
  department?: string;
  costUnit?: string;
  reward?: string;
  formFieldAnswers?: FormFieldAnswer[];
  targetGroups: TargetGroup[];
  workingTimes: WorkingTime[];
  locations: Location[];
  contractPeriod?: ContractPeriod;
  earliestEntryDate?: string;
  createdDate: Date;
  company?: Company;
  orgUnit: OrgUnit;
  responsibleUser?: ResponsibleUser;
}

export type JobOpeningType = 'DEFAULT' | 'UNSOLICITED' | 'UNDEFINED';

export interface Category {
  id: string;
  internalName: string;
  name: string;
}

export interface Location {
  id: string;
  name: string;
  country?: Country;
  latitude?: number;
  longitude?: number;
}

export interface Country {
  name: string;
  isoA2: string;
}

export interface Company {
  id: string;
  internalName: string;
  name: string;
  description1?: string;
  description2?: string;
  description3?: string;
  description4?: string;
}

export interface TargetGroup {
  internalName: string;
  name: string;
}

export interface OrgUnit {
  id: string;
  name: string;
}

export interface WorkingTime {
  internalName: string;
  name: string;
}

export interface FormFieldAnswer {
  answers: Answer[];
  answer: string;
  displayName: string;
  id: string;
  internalName: string;
  type: FormFieldAnswerType;
}

export type FormFieldAnswerType =
  | 'TEXT'
  | 'TEXT_4_120'
  | 'TEXTAREA'
  | 'TEXTAREA_MAX_1200'
  | 'FIVE_STAR_RATING'
  | 'YES_NO'
  | 'YES_NO_NOT_SPECIFIED'
  | 'SINGLE_CHOICE'
  | 'MULTI_CHOICE'
  | 'UNDEFINED';

export interface Answer {
  id: string;
  internalName: string;
  answer: string;
}

export interface ContractPeriod {
  internalName: string;
  name: string;
}

export interface ResponsibleUser {
  salutation: Salutation;
  title?: string;
  firstName: string;
  lastName: string;
  email: string;
  division?: string;
  telephone?: string;
  street?: string;
  number?: string;
  additionalAddress?: string;
  zipCode?: string;
  city?: string;
  country?: string;
}

export interface Salutation {
  internalName: SalutationType;
  name: string;
}

export type SalutationType = 'MR' | 'MX' | 'MS' | 'UNDEFINED';

export const createJobOpening = (data: JobOpeningApi): JobOpening => {
  let type: JobOpeningType;
  switch (data.type) {
    case 'DEFAULT':
      type = 'DEFAULT';
      break;
    case 'UNSOLICITED':
      type = 'UNSOLICITED';
      break;
    default:
      type = 'UNDEFINED';
  }
  return {
    id: data.id,
    name: data.name,
    type,
    categories: data.categories.map((it) => {
      return {
        id: String(it.id),
        internalName: it.internalName,
        name: it.name,
      };
    }),
    reference: data.reference,
    location: data.location,
    department: data.department,
    costUnit: data.costUnit,
    reward: data.reward,
    formFieldAnswers:
      data.formFieldAnswers &&
      data.formFieldAnswers.map((formFieldAnswer) => {
        let answerType: FormFieldAnswerType;
        switch (formFieldAnswer.type) {
          case 'TEXT':
            answerType = 'TEXT';
            break;
          case 'TEXT_4_120':
            answerType = 'TEXT_4_120';
            break;
          case 'TEXTAREA':
            answerType = 'TEXTAREA';
            break;
          case 'TEXTAREA_MAX_1200':
            answerType = 'TEXTAREA_MAX_1200';
            break;
          case 'FIVE_STAR_RATING':
            answerType = 'FIVE_STAR_RATING';
            break;
          case 'YES_NO':
            answerType = 'YES_NO';
            break;
          case 'YES_NO_NOT_SPECIFIED':
            answerType = 'YES_NO_NOT_SPECIFIED';
            break;
          case 'SINGLE_CHOICE':
            answerType = 'SINGLE_CHOICE';
            break;
          case 'MULTI_CHOICE':
            answerType = 'MULTI_CHOICE';
            break;
          default:
            answerType = 'UNDEFINED';
        }
        return {
          answers: formFieldAnswer.answers
            ? formFieldAnswer.answers.map((answer) => {
                return {
                  id: answer.id,
                  internalName: answer.internalName,
                  answer: answer.answer,
                };
              })
            : [],
          type: answerType,
          answer: formFieldAnswer.answer,
          displayName: formFieldAnswer.displayName,
          id: formFieldAnswer.id,
          internalName: formFieldAnswer.internalName,
        };
      }),
    targetGroups: data.targetGroups.map((it) => {
      return {
        internalName: it.internalName,
        name: it.name,
      };
    }),
    workingTimes: data.workingTimes
      .map((it) => {
        return {
          internalName: it.internalName,
          name: it.name,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
    locations: data.locations.map((it) => {
      return {
        id: it.id,
        name: it.name,
        country: it.country && {
          name: it.country.name,
          isoA2: it.country.isoA2,
        },
        latitude: it.latitude,
        longitude: it.longitude,
      };
    }),
    contractPeriod: data.contractPeriod && {
      internalName: data.contractPeriod.internalName,
      name: data.contractPeriod.name,
    },
    earliestEntryDate: data.earliestEntryDate,
    orgUnit: {
      id: String(data.orgUnit.id),
      name: data.orgUnit.name,
    },
    createdDate: dayjs(data.createdDate).toDate(),
    responsibleUser: data.responsibleUser && {
      salutation: {
        internalName:
          data.responsibleUser.salutation.internalName === 'MR'
            ? 'MR'
            : // eslint-disable-next-line unicorn/no-nested-ternary
            data.responsibleUser.salutation.internalName === 'MS'
            ? 'MS'
            : // eslint-disable-next-line unicorn/no-nested-ternary
            data.responsibleUser.salutation.internalName === 'MX'
            ? 'MX'
            : 'UNDEFINED',
        name: data.responsibleUser.salutation.name,
      },
      title: data.responsibleUser.title,
      firstName: data.responsibleUser.firstName,
      lastName: data.responsibleUser.lastName,
      email: data.responsibleUser.email,
      division: data.responsibleUser.division,
      telephone: data.responsibleUser.telephone,
      street: data.responsibleUser.street,
      number: data.responsibleUser.number,
      additionalAddress: data.responsibleUser.additionalAddress,
      zipCode: data.responsibleUser.zipCode,
      city: data.responsibleUser.city,
      country: data.responsibleUser.country,
    },
    company: data.company && {
      id: data.company.id,
      internalName: data.company.internalName,
      name: data.company.name,
      description1: data.company.description1,
      description2: data.company.description2,
      description3: data.company.description3,
      description4: data.company.description4,
    },
  };
};
